import { RawDraftContentState } from 'draft-js';
import { isNull } from 'lodash/fp';
import React, { useCallback, useMemo } from 'react';
import { Options } from 'react-select';

import { useAppSelector } from '../../../hooks/react-redux';
import { getAllBasicUsers } from '../../admin/users/store';
import { Dropdown, DropdownOption } from '../dropdown/Dropdown';
import { isEmpty, WYSIWYG } from '../wysiwyg/WYSIWYG';
import styles from './Report.module.scss';

interface ReportEmailContentProps {
    updateSelectedUserIds: (userIds: number[]) => void;
    selectedUserIds: number[];
    emailBody: RawDraftContentState | null;
    updateEmailBody: (value: RawDraftContentState | null) => void;
    testId: string;
}

export const ReportEmailContent: React.FC<ReportEmailContentProps> = ({ updateSelectedUserIds, selectedUserIds, emailBody, updateEmailBody, testId }) => {
    const allUsers = useAppSelector(getAllBasicUsers);
    const usernameOptions = useMemo(() => allUsers.map(({ userId, username }) => ({ value: userId.toString(), label: username })), [allUsers]);
    const selectedUsers = useMemo(() => {
        if (selectedUserIds) {
            return usernameOptions.filter(({ value }) => selectedUserIds.includes(parseInt(value)));
        }
        return null;
    }, [selectedUserIds, usernameOptions]);

    const setEmailUserIds = useCallback((value: DropdownOption | Options<DropdownOption> | null) => {
        let userIds: number[] = [];
        if (!isNull(value)) {
            userIds = (value as Options<DropdownOption>).map(({ value }) => parseInt(value));
        }
        updateSelectedUserIds(userIds);
    }, [updateSelectedUserIds]);

    const setEmailBody = useCallback((rawValue: RawDraftContentState) => {
        if (isEmpty(rawValue)) {
            if (!isNull(emailBody)) {
                updateEmailBody(null);
            }
        } else {
            updateEmailBody(rawValue);
        }
    }, [emailBody, updateEmailBody]);

    return (
        <div className={styles.emailContent} data-testid={`${testId}-wrapper`}>
            <div className={styles.emailUsersWrapper} data-testid={`${testId}-users-wrapper`}>
                <div className={styles.emailContentLabel} data-testid={`${testId}-users-label`}>To: </div>
                <div className={styles.emailUsersDropdown}>
                    <Dropdown
                        value={selectedUsers}
                        options={usernameOptions}
                        onChange={val => setEmailUserIds(val)}
                        menuPortalTarget={document.body}
                        testId={`${testId}-email-report-users`}
                        isMulti={true}
                        isClearable={true}
                        maxControlHeight='fit-content'
                    />
                </div>
            </div>
            <div className={styles.emailBodyWrapper} data-testid={`${testId}-email-report-body-wrapper`}>
                <WYSIWYG
                    content={emailBody}
                    updateContent={val => setEmailBody(val)}
                    height='100%'
                    maxHeight='100%'
                    minHeight='100%'
                />
            </div>
        </div>
    );
};
